export const groupOutletsByCity = (outlets, languageId = '1') => {
    if (!outlets || !Array.isArray(outlets)) return {};

    return outlets.reduce((groups, outlet) => {
        // Handle different outlet data structures
        let city = 'Unknown City';
        
        // Try to get city from the translations first (most common in the data)
        if (outlet.outlet_data?.outlet_address?.translations?.city?.[languageId]) {
            city = outlet.outlet_data.outlet_address.translations.city[languageId];
        } else if (outlet.outlet_address?.translations?.city?.[languageId]) {
            city = outlet.outlet_address.translations.city[languageId];
        } else if (outlet.outlet_data?.outlet_address?.city) {
            city = outlet.outlet_data.outlet_address.city;
        } else if (outlet.outlet_address?.city) {
            city = outlet.outlet_address.city;
        } else if (outlet.address?.city) {
            city = outlet.address.city;
        }

        if (!groups[city]) {
            groups[city] = [];
        }
        groups[city].push(outlet);
        return groups;
    }, {});
};

export const extractCityFromOutlet = (outlet, languageId = '1') => {
    // Try to get city from translations first (most common in the data)
    if (outlet.outlet_data?.outlet_address?.translations?.city?.[languageId]) {
        return outlet.outlet_data.outlet_address.translations.city[languageId];
    } else if (outlet.outlet_address?.translations?.city?.[languageId]) {
        return outlet.outlet_address.translations.city[languageId];
    }
    
    // Fallback to non-translated city
    return outlet.outlet_data?.outlet_address?.city || 
           outlet.outlet_address?.city || 
           outlet.address?.city ||
           'Unknown City';
};

export const getOutletDisplayName = (outlet, languageId) => {
    if (outlet.inputDisplay) {
        return outlet.inputDisplay;
    }
    
    if (outlet.translations?.name?.[languageId]) {
        return outlet.translations.name[languageId];
    }
    
    if (outlet.outlet_data?.translations?.name?.[languageId]) {
        return outlet.outlet_data.translations.name[languageId];
    }
    
    return outlet.name || 'Unknown Outlet';
};

export const getOutletId = (outlet) => {
    return outlet.value || outlet.outlet_id || outlet.id;
};

export const initializeCitySelection = (groupedOutlets, preSelectedIds = []) => {
    const selectedSet = new Set(preSelectedIds.map(id => String(id)));
    const citySelection = {};
    Object.keys(groupedOutlets).forEach(city => {
        citySelection[city] = {
            selected: false,
            outlets: {}
        };
        groupedOutlets[city].forEach(outlet => {
            const outletId = getOutletId(outlet);
            citySelection[city].outlets[outletId] = selectedSet.has(String(outletId));
        });

        // Compute city-level selection state
        const allOutlets = Object.values(citySelection[city].outlets);
        const selectedCount = allOutlets.filter(Boolean).length;
        if (selectedCount === 0) {
            citySelection[city].selected = false;
        } else if (selectedCount === allOutlets.length) {
            citySelection[city].selected = true;
        } else {
            citySelection[city].selected = 'partial';
        }
    });
    return citySelection;
};

export const toggleCitySelection = (citySelection, city, selected) => {
    const newSelection = { ...citySelection };
    newSelection[city] = {
        selected: selected,
        outlets: {}
    };
    
    // Toggle all outlets in the city
    Object.keys(citySelection[city].outlets).forEach(outletId => {
        newSelection[city].outlets[outletId] = selected;
    });
    
    return newSelection;
};

export const toggleOutletSelection = (citySelection, city, outletId, selected) => {
    const newSelection = { ...citySelection };
    newSelection[city].outlets[outletId] = selected;
    
    // Update city selection based on outlet selections
    const allOutlets = Object.values(newSelection[city].outlets);
    const selectedCount = allOutlets.filter(isSelected => isSelected).length;
    
    if (selectedCount === 0) {
        newSelection[city].selected = false;
    } else if (selectedCount === allOutlets.length) {
        newSelection[city].selected = true;
    } else {
        newSelection[city].selected = 'partial';
    }
    
    return newSelection;
};

export const getSelectedOutletIds = (citySelection) => {
    const selectedIds = [];
    
    Object.keys(citySelection).forEach(city => {
        Object.entries(citySelection[city].outlets).forEach(([outletId, isSelected]) => {
            if (isSelected) {
                selectedIds.push(outletId);
            }
        });
    });
    
    return selectedIds;
};

export const getSelectionSummary = (citySelection, groupedOutlets) => {
    let totalCities = 0;
    let selectedCities = 0;
    let partialCities = 0;
    let totalOutlets = 0;
    let selectedOutlets = 0;
    
    Object.keys(citySelection).forEach(city => {
        totalCities++;
        const cityData = citySelection[city];
        
        if (cityData.selected === true) {
            selectedCities++;
        } else if (cityData.selected === 'partial') {
            partialCities++;
        }
        
        Object.values(cityData.outlets).forEach(isSelected => {
            totalOutlets++;
            if (isSelected) {
                selectedOutlets++;
            }
        });
    });
    
    return {
        totalCities,
        selectedCities,
        partialCities,
        totalOutlets,
        selectedOutlets
    };
};

export const selectAllOutlets = (citySelection) => {
    const newSelection = {};
    
    Object.keys(citySelection).forEach(city => {
        newSelection[city] = {
            selected: true,
            outlets: {}
        };
        Object.keys(citySelection[city].outlets).forEach(outletId => {
            newSelection[city].outlets[outletId] = true;
        });
    });
    
    return newSelection;
};

export const deselectAllOutlets = (citySelection) => {
    const newSelection = {};
    
    Object.keys(citySelection).forEach(city => {
        newSelection[city] = {
            selected: false,
            outlets: {}
        };
        Object.keys(citySelection[city].outlets).forEach(outletId => {
            newSelection[city].outlets[outletId] = false;
        });
    });
    
    return newSelection;
};

export const filterOutletsBySearch = (groupedOutlets, searchTerm, languageId = '1') => {
    if (!searchTerm) return groupedOutlets;
    
    const filtered = {};
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    Object.entries(groupedOutlets).forEach(([city, outlets]) => {
        // Check if city matches
        if (city.toLowerCase().includes(lowerSearchTerm)) {
            filtered[city] = outlets;
        } else {
            // Check if any outlet in the city matches
            const matchingOutlets = outlets.filter(outlet => {
                const outletName = getOutletDisplayName(outlet, languageId).toLowerCase();
                return outletName.includes(lowerSearchTerm);
            });
            
            if (matchingOutlets.length > 0) {
                filtered[city] = matchingOutlets;
            }
        }
    });
    
    return filtered;
};